
































































import Vue from 'vue';
import {Component, Prop} from "vue-property-decorator";
import AsiPageTitle from "@/components/common/AsiPageTitle.vue";
import {Icon} from "@/helpers/icons";
import UserModel from "@/models/user/User";
import AsiTabs from "@/components/common/AsiTabs";
import {CompanyState, CompanyType, Permissions, Roles, UserState} from "@/helpers/constants";
import AsiMenuItem from "@/components/common/AsiMenuItem.vue";
import UserTabDetails from "@/components/user/UserTabDetails.vue";
import UserTabReports from "@/components/user/UserTabReports.vue";
import UserTabPlanningEntries from "@/components/user/UserTabPlanningEntries.vue";
import UserTabRights from "@/components/user/UserTabRights.vue";
import AsiConfirmDialog from "@/components/common/AsiConfirmDialog.vue";
import UserSimple from "@/models/user/UserSimple";
import {SnackbarMessage} from "@/models/SnackbarMessage";
import AsiDialogAvatarUpload from "@/components/common/AsiDialogAvatarUpload.vue";
import UserUpdateDialogUsername from "@/components/user/UserUpdateDialogUsername.vue";
import UserUpdateDialogPassword from "@/components/user/UserUpdateDialogPassword.vue";
import ReportCreateDialog from "@/components/report/ReportCreateDialog.vue";

@Component({
	components: {
		UserUpdateDialogPassword,
		UserUpdateDialogUsername,
		ReportCreateDialog,
		AsiDialogAvatarUpload,
		AsiConfirmDialog,
		UserTabRights,
		UserTabPlanningEntries,
		UserTabReports,
		UserTabDetails,
		AsiMenuItem,
		AsiTabs, AsiPageTitle
	}
})
export default class User extends Vue {

	@Prop({type: String, required: true})
	public id!: string;

	private icons = Icon;
	private user: UserModel | null = null;
	private roles: Roles[] = [];
	private permissions: Permissions[] = [];
	private activeTab: number = 0;
	private loading: number = 0;
	private avatarDialogOpen: boolean = false;
	private usernameDialogOpen: boolean = false;
	private passwordDialogOpen: boolean = false;
	private reportCreateDialogOpen: boolean = false;

	private get userArchived(): boolean {
		return this.user?.state === UserState.archived ?? false;
	}

	private get fullName(): string | null {
		return UserModel.fullName(this.user);
	}

	private get companyName(): string | null {
		return this.user?.company.name ?? null;
	}

	private get hasReports(): boolean {
		return this.user?.hasReports ?? true;
	}

	private get isInternal(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightInternal);
	}

	private get isOffice(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightOffice);
	}

	private get isMasterData(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightMasterData);
	}

	private get belongsToInternalCompany(): boolean {
		return this.user?.company.type === CompanyType.internal ?? false;
	}

	private get isOwnProfile(): boolean {
		if (this.user === null) return false;
		return this.user.id === this.$store.getters['user/userId'];
	}

	private get isCompanyArchived(): boolean {
		return this.user?.company.state === CompanyState.archived ?? true;
	}

	private get isAdmin(): boolean {
		return this.$store.getters['user/isAdmin'];
	}

	private get avatarUrl(): string | null {
		return UserSimple.avatarUrlMedium(this.user);
	}

	public mounted(): void {
		this.loadUser();
	}

	private usernameChanged(): void {
		this.usernameDialogOpen = false;
		this.loadUser();
	}

	private passwordChanged(): void {
		this.passwordDialogOpen = false;
		this.loadUser();
	}

	private loadUser(): void {
		this.loading++;
		this.$userService.user(this.id)
			.then(user => {
				this.user = user;
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => {
				this.loading--;
			});

		this.loadRights();
	}

	private loadRights(): void {
		this.loading++;
		this.$userService.roles(this.id)
			.then((roles: Roles[]) => {
				this.roles = roles;
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => {
				this.loading--;
			});

		this.loading++;
		this.$userService.permissions(this.id)
			.then((permissions: Permissions[]) => {
				this.permissions = permissions;
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => {
				this.loading--;
			});
	}

	private avatarChanged(): void {
		if (this.isOwnProfile) {
			this.$store.dispatch('user/loadLoggedInUserData');
		}
		this.loadUser();
	}

	private resetLoginData(): void {
		if (this.user === null) return;

		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.user === null) return;

			this.loading++;
			this.$authService.resetPassword(this.user.username)
				.then(() => {
					this.$store.commit('ui/showSnackbar', {
						message: this.$t('snackbar.resetPasswordSuccessful'),
						color: 'success',
					} as SnackbarMessage);
				})
				.catch(() => {
					this.$store.commit('ui/showSnackbar', {
						message: this.$t('snackbar.updateError'),
						color: 'error',
					} as SnackbarMessage);
				})
				.finally(() => this.loading--);
		});
	}

	private setStateActive(): void {
		this.setState(UserState.active);
	}

	private setStateArchived(): void {
		this.setState(UserState.archived);
	}

	private setState(requestedState: UserState): void {
		if (this.user === null) return;

		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.user === null) return;
			this.loading++;
			this.$userService.state(this.user.id, requestedState)
				.then(() => {
					this.loadUser();
				})
				.catch(() => {
					this.$store.commit('ui/showSnackbar', {
						message: this.$t('snackbar.updateError'),
						color: 'error',
					} as SnackbarMessage);
				})
				.finally(() => {
					this.loading--;
				});
		});
	}

	private deleteUser(): void {
		if (this.user === null || this.user.hasReports) return;

		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.user === null) return;

			this.loading++;
			this.$userService.delete(this.user.id)
				.then(() => {
					this.$router.push({name: 'Users'})
				})
				.catch(() => {
					this.$store.commit('ui/showSnackbar', {
						message: this.$t('snackbar.deleteError'),
						color: 'error',
					} as SnackbarMessage);
				})
				.finally(() => {
					this.loading--;
				});
		});
	}

}
